// hooks/useIssueFeed.js
import { useEffect, useMemo, useState } from 'react';
import { issueCacheSingleton } from '../cache/issueCache';

/**
 * Hook to access fast-loading issue feed for a scope
 * @param {import('../types/issues').Scope} scope - 'my' or 'others'
 * @param {string} authToken - Authorization token
 * @returns {{issues: ReadonlyArray<import('../types/issues').NormalizedIssue>, loading: boolean, refresh: () => void}}
 */
export function useIssueFeed(scope, authToken) {
  const cache = useMemo(() => issueCacheSingleton.get(scope), [scope]);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    // Set auth token on cache
    cache.setAuthToken(authToken);

    const unsub = cache.subscribe(() => setVersion(v => v + 1));
    if (cache.list.length === 0) cache.start(true); // kicks off background load
    return unsub;
  }, [cache, authToken]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => ({
    issues: [...cache.list], // Create new array reference so React detects changes
    loading: cache.loading,
    refresh: () => cache.refresh()
  }), [cache, version]); // version forces re-render when cache updates
}
